import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Role } from '@kia/shared';
import { api, setAuthToken, setOnUnauthorized } from '../api/client';

export interface SessionUser {
  username: string;
  role: Role;
  name: string;
}
interface Session {
  token: string;
  user: SessionUser;
}

export type LoginResult =
  { ok: true } | { ok: false; message: string; mfaRequired?: boolean; mfaInvalid?: boolean };

interface AuthContextValue {
  user: SessionUser | null;
  login: (username: string, password: string, otp?: string) => Promise<LoginResult>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const STORAGE_KEY = 'ct.session';

function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    const restored = loadSession();
    // Set the token SYNCHRONOUSLY during init so first-render fetches (screens that load on mount)
    // carry it — otherwise a child effect can fire a tokenless request before the effect below runs,
    // getting a 401 that auto-logs-out on reload.
    setAuthToken(restored?.token ?? null);
    return restored;
  });

  // Keep the API client's bearer token in sync on subsequent login/logout.
  useEffect(() => {
    setAuthToken(session?.token ?? null);
  }, [session]);

  const logout = useCallback(() => {
    void api('/api/logout', { method: 'POST' }); // best effort
    setSession(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  // Auto-logout if the server rejects an authenticated request (expired session).
  useEffect(() => {
    setOnUnauthorized(logout);
    return () => setOnUnauthorized(null);
  }, [logout]);

  const login = useCallback(
    async (username: string, password: string, otp?: string): Promise<LoginResult> => {
      let res;
      try {
        res = await api<{ token: string; username: string; role: Role; name: string }>(
          '/api/login',
          {
            method: 'POST',
            body: JSON.stringify(otp ? { username, password, otp } : { username, password }),
          },
        );
      } catch {
        return { ok: false, message: 'سرور در دسترس نیست — اتصال را بررسی کنید.' };
      }
      if (res.ok && 'token' in (res.body as object)) {
        const b = res.body as { token: string; username: string; role: Role; name: string };
        const next: Session = {
          token: b.token,
          user: { username: b.username, role: b.role, name: b.name },
        };
        // Same reasoning as the init path above: set the token SYNCHRONOUSLY before the
        // re-render. Newly-mounted screens fire their on-mount fetches in child effects,
        // which run BEFORE this provider's sync effect — without this line the first
        // fetch after login goes out tokenless, 401s, and auto-logs the user back out.
        setAuthToken(b.token);
        setSession(next);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          // ignore storage errors
        }
        return { ok: true };
      }
      const err = res.body as { message?: string; error?: string };
      // 2FA signals: the password was accepted but a second factor is needed (or the code was wrong).
      if (err.error === 'mfa_required') return { ok: false, message: '', mfaRequired: true };
      if (err.error === 'mfa_invalid') {
        return {
          ok: false,
          message: err.message ?? 'کد نادرست است.',
          mfaRequired: true,
          mfaInvalid: true,
        };
      }
      return { ok: false, message: err.message ?? 'ورود ناموفق بود.' };
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user: session?.user ?? null, login, logout }),
    [session, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
